/**
 * Design tokens and constants for the Trainer Portal
 * Centralizes all shared configuration values
 */

// Color palette - matches Tailwind config
export const colors = {
  primary: {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
  },
  semantic: {
    success: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
    error: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
    warning: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
    info: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  },
}

// Quick action button color classes
export const actionColors = {
  blue: 'bg-blue-50 text-blue-600',
  green: 'bg-green-50 text-green-600',
  purple: 'bg-purple-50 text-purple-600',
  teal: 'bg-teal-50 text-teal-600',
  orange: 'bg-orange-50 text-orange-600',
  red: 'bg-red-50 text-red-600',
  gray: 'bg-gray-50 text-gray-600',
  primary: 'bg-primary-50 text-primary-600',
}

// Memory type configuration
export const memoryTypes = {
  medical: {
    label: 'Medical',
    color: 'text-red-600 bg-red-50',
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
  },
  preference: {
    label: 'Preference',
    color: 'text-purple-600 bg-purple-50',
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  personal: {
    label: 'Personal',
    color: 'text-blue-600 bg-blue-50',
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  goal: {
    label: 'Goal',
    color: 'text-green-600 bg-green-50',
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  behavioral: {
    label: 'Behavioral',
    color: 'text-orange-600 bg-orange-50',
    iconColor: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
  situational: {
    label: 'Situational',
    color: 'text-gray-600 bg-gray-50',
    iconColor: 'text-gray-600',
    bgColor: 'bg-gray-50',
  },
}

// Progress photo change ratings
export const changeRatings = {
  significant_positive: {
    label: 'Great Progress',
    bgClass: 'bg-green-100',
    textClass: 'text-green-800',
  },
  moderate: {
    label: 'Good Progress',
    bgClass: 'bg-blue-100',
    textClass: 'text-blue-800',
  },
  minimal: {
    label: 'Steady',
    bgClass: 'bg-gray-100',
    textClass: 'text-gray-700',
  },
  concerning: {
    label: 'Needs Attention',
    bgClass: 'bg-amber-100',
    textClass: 'text-amber-800',
  },
}

// Training types
export const trainingTypes = {
  in_person: { label: 'In Person', color: 'bg-blue-100 text-blue-700' },
  online: { label: 'Online', color: 'bg-purple-100 text-purple-700' },
  hybrid: { label: 'Hybrid', color: 'bg-teal-100 text-teal-700' },
}

// Client status
export const clientStatus = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-600' },
}

// Knowledge base categories
export const knowledgeCategories = {
  training_philosophy: 'Training Philosophy',
  exercise_technique: 'Exercise Technique',
  nutrition: 'Nutrition',
  recovery: 'Recovery',
  motivation: 'Motivation',
  program_design: 'Program Design',
  client_management: 'Client Management',
  faq: 'FAQ',
}

// Session statuses
export const sessionStatuses = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' },
  confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Completed', color: 'bg-gray-100 text-gray-600' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
  no_show: { label: 'No Show', color: 'bg-amber-100 text-amber-700' },
}

// Muscle groups for exercises
export const muscleGroups = [
  'chest',
  'back',
  'shoulders',
  'biceps',
  'triceps',
  'forearms',
  'core',
  'quadriceps',
  'hamstrings',
  'glutes',
  'calves',
  'full_body',
  'cardio',
]

// Equipment types
export const equipmentTypes = [
  'barbell',
  'dumbbell',
  'kettlebell',
  'cable',
  'machine',
  'bodyweight',
  'resistance_band',
  'medicine_ball',
  'cardio_machine',
  'other',
]

// Common Tailwind class combinations
export const tw = {
  // Cards
  card: 'bg-white rounded-xl shadow-sm',
  cardPadded: 'bg-white rounded-xl shadow-sm p-4',
  cardHover: 'bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow',

  // Buttons
  btnPrimary: 'bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors',
  btnSecondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors',
  btnGhost: 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors',
  btnDanger: 'bg-red-600 text-white hover:bg-red-700 transition-colors',

  // Inputs
  input: 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
  inputError: 'w-full px-4 py-3 border border-red-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent',

  // Text
  heading1: 'text-2xl font-bold text-gray-900',
  heading2: 'text-xl font-semibold text-gray-900',
  heading3: 'text-lg font-semibold text-gray-900',
  bodyText: 'text-base text-gray-700',
  mutedText: 'text-sm text-gray-500',
  tinyText: 'text-xs text-gray-400',

  // Layout
  pageContainer: 'min-h-screen bg-gray-100',
  pageContent: 'px-4 py-4 space-y-4',

  // Badges
  badge: 'px-2 py-0.5 rounded-full text-xs font-medium',
  badgeSmall: 'px-1.5 py-0.5 rounded text-[10px] font-medium',

  // Flex patterns
  flexCenter: 'flex items-center justify-center',
  flexBetween: 'flex items-center justify-between',
  flexStart: 'flex items-center gap-3',
}

// Animation classes
export const animations = {
  fadeIn: 'animate-fade-in',
  pulse: 'animate-pulse',
  spin: 'animate-spin',
  typingDot: 'animate-typing-dot',
}

// Z-index layers
export const zIndex = {
  dropdown: 'z-10',
  sticky: 'z-20',
  modal: 'z-50',
  tooltip: 'z-60',
  toast: 'z-70',
}

// Breakpoints (for reference, use Tailwind's responsive prefixes)
export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
}

// API cache durations (in ms)
export const cacheDurations = {
  short: 1 * 60 * 1000,    // 1 minute
  standard: 5 * 60 * 1000, // 5 minutes
  long: 15 * 60 * 1000,    // 15 minutes
}

// Helper function to get memory type config
export function getMemoryType(type) {
  return memoryTypes[type] || memoryTypes.personal
}

// Helper function to get change rating info
export function getChangeRating(rating) {
  return changeRatings[rating] || null
}

// Helper function to get training type info
export function getTrainingType(type) {
  return trainingTypes[type] || { label: type?.replace('_', ' ') || '-', color: 'bg-gray-100 text-gray-600' }
}

// Helper function to get session status info
export function getSessionStatus(status) {
  return sessionStatuses[status] || { label: status || '-', color: 'bg-gray-100 text-gray-600' }
}
